/**
 * Stock Take/Audit Mode Dialog Component
 *
 * Interface for performing physical inventory counts and audits.
 * Supports barcode scanning, manual counting, and discrepancy reporting.
 */

import { useState, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  Tab,
  Tabs,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import CloseIcon from '@mui/icons-material/Close';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import SearchIcon from '@mui/icons-material/Search';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';
import type { InventoryItem } from '@/types';
import { BarcodeScannerDialog } from './BarcodeScannerDialog';
import { useTranslation } from 'react-i18next';
import { formatDate, intlLocale } from '@/utils/locale';

export interface StockTakeItem extends InventoryItem {
  partId: string;
  warehouseId: string;
  binLocationId: string | null;
}

interface StockTakeDialogProps {
  open: boolean;
  onClose: () => void;
  items: StockTakeItem[];
  onComplete: (results: StockTakeResult[]) => void;
}

export interface StockTakeResult {
  warehouseId: string;
  binLocationId: string | null;
  itemId: string;
  systemQuantity: number;
  countedQuantity: number;
  discrepancy: number;
  notes?: string;
  status: 'match' | 'discrepancy' | 'missing';
}

interface CountedItem extends StockTakeItem {
  countedQuantity: number;
  counted: boolean;
  notes: string;
}

type StockTakeStep = 'setup' | 'counting' | 'review';
type ViewMode = 'all' | 'pending' | 'counted' | 'discrepancies';

export function StockTakeDialog({ open, onClose, items, onComplete }: StockTakeDialogProps) {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const [step, setStep] = useState<StockTakeStep>('setup');
  const [viewMode, setViewMode] = useState<ViewMode>('pending');
  const [countedItems, setCountedItems] = useState<CountedItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<CountedItem | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [sessionName, setSessionName] = useState('');
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  useEffect(() => {
    if (open && step === 'setup') {
      const now = new Date();
      setSessionName(t('stockTake.defaultSessionName', {
        date: formatDate(now),
        time: now.toLocaleTimeString(intlLocale(), { hour: '2-digit', minute: '2-digit' }),
      }));
      setCountedItems(
        items.map((item) => ({
          ...item,
          countedQuantity: 0,
          counted: false,
          notes: '',
        }))
      );
    }
    // `t` is left out on purpose: a language change mid-count must not reset
    // the counted quantities, and the default name only needs the language
    // the dialog opened in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, items, step]);

  const isDirty = step !== 'setup' || countedItems.some((i) => i.counted);

  const handleStartCounting = () => {
    setStep('counting');
  };

  const handleCountItem = (itemId: string, quantity: number, notes?: string) => {
    setCountedItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? { ...item, countedQuantity: quantity, counted: true, notes: notes || '' }
          : item
      )
    );
    setSelectedItem(null);
  };

  const handleQuickCount = (item: CountedItem) => {
    setSelectedItem(item);
  };

  const handleScanBarcode = (barcode: string) => {
    const matches = countedItems.filter((item) => item.partNumber === barcode || item.barcode === barcode);
    if (matches.length === 1 && matches[0]) {
      setSelectedItem(matches[0]);
      setSearchQuery('');
    } else {
      setSearchQuery(barcode);
    }
  };

  const handleReview = () => {
    setStep('review');
  };

  const handleComplete = () => {
    const results: StockTakeResult[] = countedItems
      .filter((item) => item.counted)
      .map((item) => ({
        itemId: item.partId,
        warehouseId: item.warehouseId,
        binLocationId: item.binLocationId,
        systemQuantity: item.quantity,
        countedQuantity: item.countedQuantity,
        discrepancy: item.countedQuantity - item.quantity,
        notes: item.notes,
        status:
          item.countedQuantity === item.quantity
            ? 'match'
            : item.countedQuantity === 0
            ? 'missing'
            : 'discrepancy',
      }));

    onComplete(results);
    doClose();
  };

  const doClose = () => {
    setStep('setup');
    setViewMode('pending');
    setCountedItems([]);
    setSearchQuery('');
    setSelectedItem(null);
    onClose();
  };

  const handleClose = () => {
    if (isDirty) {
      setShowDiscardConfirm(true);
    } else {
      doClose();
    }
  };

  const filteredItems = countedItems.filter((item) => {
    const matchesSearch =
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.partNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      !!item.barcode?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesView =
      viewMode === 'all' ||
      (viewMode === 'pending' && !item.counted) ||
      (viewMode === 'counted' && item.counted) ||
      (viewMode === 'discrepancies' && item.counted && item.countedQuantity !== item.quantity);

    return matchesSearch && matchesView;
  });

  const countedCount = countedItems.filter((i) => i.counted).length;
  const discrepancyCount = countedItems.filter(
    (i) => i.counted && i.countedQuantity !== i.quantity
  ).length;
  const progress = (countedCount / countedItems.length) * 100;

  const getDiscrepancyColor = (item: CountedItem) => {
    if (!item.counted) return 'default';
    if (item.countedQuantity === item.quantity) return 'success';
    if (item.countedQuantity === 0) return 'error';
    return 'warning';
  };

  const steps = [t('stockTake.steps.setup'), t('stockTake.steps.counting'), t('stockTake.steps.review')];

  return (
    <>
      <ResponsiveDialog mobileTitle={t('stockTake.title')} open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">{t('stockTake.title')}</Typography>
            <Tooltip title={tc('close')}>
              <IconButton onClick={handleClose} size="small" aria-label={tc('close')}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>

        <Box sx={{ px: 3, mb: 2 }}>
          <Stepper activeStep={step === 'setup' ? 0 : step === 'counting' ? 1 : 2}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <DialogContent sx={{ minHeight: 500 }}>
          {step === 'setup' && (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                {t('stockTake.info')}
              </Alert>

              <TextField
                fullWidth
                autoFocus
                label={t('stockTake.sessionName')}
                value={sessionName}
                onChange={(e) => setSessionName(e.target.value)}
                sx={{ mb: 3 }}
              />

              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={600} gutterBottom>
                  {countedItems.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('stockTake.partsToCount')}
                </Typography>
              </Paper>
            </Box>
          )}

          {step === 'counting' && (
            <Box>
              {/* Progress */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {t('stockTake.progress')}
                  </Typography>
                  <Typography variant="body2">
                    {countedCount} / {countedItems.length}
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 1 }} />
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={t('stockTake.counted', { count: countedCount })}
                    color="success"
                    size="small"
                  />
                  {discrepancyCount > 0 && (
                    <Chip
                      icon={<WarningIcon />}
                      label={t('stockTake.discrepancies', { count: discrepancyCount })}
                      color="warning"
                      size="small"
                    />
                  )}
                </Box>
              </Paper>

              {/* Search and Scanner */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t('stockTake.searchPart')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <Button
                  variant="outlined"
                  startIcon={<QrCodeScannerIcon />}
                  onClick={() => setScannerOpen(true)}
                >
                  {t('stockTake.scan')}
                </Button>
              </Box>

              {/* View Mode Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={viewMode} onChange={(_, v) => setViewMode(v)} variant="scrollable" scrollButtons="auto">
                  <Tab label={`${t('stockTake.tabAll')} (${countedItems.length})`} value="all" />
                  <Tab label={`${t('stockTake.tabPending')} (${countedItems.length - countedCount})`} value="pending" />
                  <Tab label={`${t('stockTake.tabCounted')} (${countedCount})`} value="counted" />
                  <Tab label={`${t('stockTake.tabDiscrepancies')} (${discrepancyCount})`} value="discrepancies" />
                </Tabs>
              </Box>

              {/* Items List */}
              <List disablePadding sx={{ maxHeight: 400, overflow: 'auto' }}>
                {filteredItems.map((item, index) => (
                  <Box key={item.id}>
                    {index > 0 && <Divider />}
                    <ListItem
                      sx={{
                        py: 1.5,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                      onClick={() => handleQuickCount(item)}
                    >
                      <ListItemText
                        disableTypography
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {item.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">{item.location}</Typography>
                            <Chip
                              label={item.partNumber}
                              size="small"
                              variant="outlined"
                              sx={{ fontFamily: 'monospace', height: 20, fontSize: '0.7rem' }}
                            />
                            {item.counted && (
                              <Chip
                                icon={
                                  item.countedQuantity === item.quantity ? (
                                    <CheckCircleIcon />
                                  ) : (
                                    <WarningIcon />
                                  )
                                }
                                label={
                                  item.countedQuantity === item.quantity
                                    ? t('stockTake.matches')
                                    : t('stockTake.discrepancy', { diff: `${item.countedQuantity - item.quantity > 0 ? '+' : ''}${item.countedQuantity - item.quantity}` })
                                }
                                size="small"
                                color={getDiscrepancyColor(item)}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {t('stockTake.systemQty', { qty: item.quantity })}
                            </Typography>
                            {item.counted && (
                              <Typography variant="caption" fontWeight={600}>
                                {t('stockTake.countedQty', { qty: item.countedQuantity })}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  </Box>
                ))}
                {filteredItems.length === 0 && (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">{t('stockTake.noPartsToShow')}</Typography>
                  </Box>
                )}
              </List>

              {/* Quick Count Panel */}
              {selectedItem && (
                <Paper
                  elevation={3}
                  sx={{
                    position: 'fixed',
                    bottom: 20,
                    left: { xs: 16, md: '50%' },
                    right: { xs: 16, md: 'auto' },
                    transform: { xs: 'none', md: 'translateX(-50%)' },
                    p: { xs: 2, md: 3 },
                    minWidth: { md: 400 },
                    zIndex: 1300,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {selectedItem.name}
                  </Typography>
                  <Typography variant="body2" gutterBottom>{selectedItem.location}</Typography>
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    {t('stockTake.systemQty', { qty: selectedItem.quantity })}
                  </Typography>
                  <TextField
                    autoFocus
                    fullWidth
                    type="number"
                    label={t('stockTake.countedQuantity')}
                    defaultValue={selectedItem.countedQuantity}
                    sx={{ mt: 2, mb: 2 }}
                    slotProps={{
                      htmlInput: { min: 0 },
                      input: { endAdornment: 'kpl' },
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        const input = e.target as HTMLInputElement;
                        handleCountItem(selectedItem.id, parseInt(input.value) || 0);
                      }
                    }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label={t('stockTake.notesOptional')}
                    defaultValue={selectedItem.notes}
                    multiline
                    rows={2}
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button onClick={() => setSelectedItem(null)} color="inherit">
                      {tc('cancel')}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => {
                        const quantityInput = document.querySelector<HTMLInputElement>(
                          'input[type="number"]'
                        );
                        const notesInput = document.querySelector<HTMLTextAreaElement>(
                          'textarea'
                        );
                        handleCountItem(
                          selectedItem.id,
                          parseInt(quantityInput?.value || '0') || 0,
                          notesInput?.value
                        );
                      }}
                    >
                      {tc('save')}
                    </Button>
                  </Box>
                </Paper>
              )}
            </Box>
          )}

          {step === 'review' && (
            <Box>
              <Alert severity={discrepancyCount > 0 ? 'warning' : 'success'} sx={{ mb: 3 }}>
                {discrepancyCount > 0
                  ? t('stockTake.reviewDiscrepancies', { count: discrepancyCount })
                  : t('stockTake.reviewAllMatch')}
              </Alert>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('stockTake.colPart')}</TableCell>
                      <TableCell align="right">{t('stockTake.colSystem')}</TableCell>
                      <TableCell align="right">{t('stockTake.colCounted')}</TableCell>
                      <TableCell align="right">{t('stockTake.colDifference')}</TableCell>
                      <TableCell>{t('stockTake.colStatus')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {countedItems
                      .filter((item) => item.counted)
                      .map((item) => {
                        const diff = item.countedQuantity - item.quantity;
                        return (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {item.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">{item.location}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.partNumber}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{item.quantity}</TableCell>
                            <TableCell align="right">{item.countedQuantity}</TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                color={
                                  diff === 0
                                    ? 'text.secondary'
                                    : diff > 0
                                    ? 'success.main'
                                    : 'error.main'
                                }
                              >
                                {diff > 0 ? '+' : ''}
                                {diff}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={
                                  diff === 0 ? (
                                    <CheckCircleIcon />
                                  ) : diff === -item.quantity ? (
                                    <ErrorIcon />
                                  ) : (
                                    <WarningIcon />
                                  )
                                }
                                label={
                                  diff === 0
                                    ? t('stockTake.matches')
                                    : diff === -item.quantity
                                    ? t('stockTake.statusMissing')
                                    : t('stockTake.statusDiscrepancy')
                                }
                                size="small"
                                color={getDiscrepancyColor(item)}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>

              {countedItems.length - countedCount > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {t('stockTake.uncountedWarning', { count: countedItems.length - countedCount })}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} color="inherit">
            {step === 'review' ? t('stockTake.reject') : tc('cancel')}
          </Button>
          {step === 'setup' && (
            <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={handleStartCounting}>
              {t('stockTake.startCounting')}
            </Button>
          )}
          {step === 'counting' && (
            <Button
              variant="contained"
              onClick={handleReview}
              disabled={countedCount === 0}
            >
              {t('stockTake.reviewBtn', { count: countedCount })}
            </Button>
          )}
          {step === 'review' && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleComplete}
            >
              {t('stockTake.saveStockTake')}
            </Button>
          )}
        </DialogActions>

        <BarcodeScannerDialog
          open={scannerOpen}
          onClose={() => setScannerOpen(false)}
          onScan={handleScanBarcode}
          title={t('stockTake.scanPart')}
        />
      </ResponsiveDialog>

      <ConfirmDialog
        open={showDiscardConfirm}
        title={tc('unsavedChanges')}
        message={tc('unsavedChangesMessage')}
        confirmText={tc('discard')}
        confirmColor="error"
        onConfirm={() => {
          setShowDiscardConfirm(false);
          doClose();
        }}
        onCancel={() => setShowDiscardConfirm(false)}
      />
    </>
  );
}
